import { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MapPin, Plus, Trash2, GripVertical, Camera, Check, X, 
  ChevronUp, ChevronDown, Edit2, Image, Flag, FlagTriangleRight, Circle, Upload, Loader2, Link2
} from 'lucide-react';
import { toast } from 'sonner';

const WAYPOINT_TYPES = {
  start: { label: 'Salida', color: 'bg-green-500', icon: Flag },
  finish: { label: 'Llegada', color: 'bg-red-500', icon: FlagTriangleRight },
  checkpoint: { label: 'Paso obligatorio', color: 'bg-blue-500', icon: MapPin },
  passage: { label: 'Punto de paso', color: 'bg-yellow-500', icon: Circle }
};

export default function WaypointManager({ eventId, waypoints = [], onWaypointsChange, readOnly = false }) {
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const replace = waypoints.length > 0 && window.confirm(`Hay ${waypoints.length} waypoints. ¿Reemplazarlos por los del archivo? (Cancelar = añadir a los existentes)`);
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('replace', replace ? 'true' : 'false');
      const r = await axios.post(`${API}/admin/events/${eventId}/waypoints/import`, fd);
      const ev = await axios.get(`${API}/events/${eventId}`);
      onWaypointsChange(ev.data.waypoints || []);
      const cats = Object.entries(r.data.by_category || {}).map(([c, n]) => `${c}: ${n}`).join(', ');
      toast.success(`${r.data.imported} waypoints importados${r.data.skipped ? ` (${r.data.skipped} sin coordenadas)` : ''}. ${cats}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar waypoints');
    } finally {
      setImporting(false);
    }
  };

  const handleImportUrl = async (e) => {
    e.stopPropagation();
    const url = window.prompt('Pega el enlace de Google My Maps (compartido como "Cualquier persona con el enlace") o una URL directa a un KML/KMZ/GPX/CSV:');
    if (!url || !url.trim()) return;
    const replace = waypoints.length > 0 && window.confirm(`Hay ${waypoints.length} waypoints. ¿Reemplazarlos por los del mapa? (Cancelar = añadir a los existentes)`);
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/events/${eventId}/waypoints/import-url`, { url: url.trim(), replace });
      const ev = await axios.get(`${API}/events/${eventId}`);
      onWaypointsChange(ev.data.waypoints || []);
      const cats = Object.entries(r.data.by_category || {}).map(([c, n]) => `${c}: ${n}`).join(', ');
      toast.success(`${r.data.imported} waypoints importados desde el enlace. ${cats}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar desde el enlace');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/waypoints/import-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_waypoints.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar la plantilla'); }
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWaypoint, setEditingWaypoint] = useState(null);
  const [newWaypoint, setNewWaypoint] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    is_mandatory: true,
    waypoint_type: 'checkpoint',
    radius_m: 200  // Default radius in meters
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importingGpx, setImportingGpx] = useState(false);

  // Check if start/finish already exist
  const hasStart = waypoints.some(w => w.waypoint_type === 'start');
  const hasFinish = waypoints.some(w => w.waypoint_type === 'finish');

  // Update default radius when waypoint type changes
  const handleWaypointTypeChange = (type) => {
    const defaultRadius = (type === 'start' || type === 'finish') ? 150 : 200;
    setNewWaypoint({ ...newWaypoint, waypoint_type: type, radius_m: defaultRadius });
  };

  const handleAddWaypoint = async () => {
    if (!newWaypoint.name.trim()) {
      toast.error('El nombre del waypoint es obligatorio');
      return;
    }

    // Validate only one start/finish
    if (newWaypoint.waypoint_type === 'start' && hasStart) {
      toast.error('Ya existe un waypoint de Salida');
      return;
    }
    if (newWaypoint.waypoint_type === 'finish' && hasFinish) {
      toast.error('Ya existe un waypoint de Llegada');
      return;
    }

    // Start/finish require coordinates
    if ((newWaypoint.waypoint_type === 'start' || newWaypoint.waypoint_type === 'finish') && 
        (!newWaypoint.latitude || !newWaypoint.longitude)) {
      toast.error('Los waypoints de Salida/Llegada requieren coordenadas GPS');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/events/${eventId}/waypoints`,
        {
          name: newWaypoint.name,
          description: newWaypoint.description,
          latitude: newWaypoint.latitude ? parseFloat(newWaypoint.latitude) : null,
          longitude: newWaypoint.longitude ? parseFloat(newWaypoint.longitude) : null,
          is_mandatory: newWaypoint.is_mandatory,
          waypoint_type: newWaypoint.waypoint_type,
          radius_m: parseInt(newWaypoint.radius_m) || 200
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Waypoint añadido');
      onWaypointsChange([...waypoints, response.data.waypoint]);
      setShowAddModal(false);
      setNewWaypoint({ name: '', description: '', latitude: '', longitude: '', is_mandatory: true, waypoint_type: 'checkpoint', radius_m: 200 });
    } catch (error) {
      console.error('Error adding waypoint:', error);
      toast.error(error.response?.data?.detail || 'Error al añadir waypoint');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWaypoint = async () => {
    if (!editingWaypoint.name.trim()) {
      toast.error('El nombre del waypoint es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/events/${eventId}/waypoints/${editingWaypoint.id}`,
        {
          name: editingWaypoint.name,
          description: editingWaypoint.description,
          latitude: editingWaypoint.latitude ? parseFloat(editingWaypoint.latitude) : null,
          longitude: editingWaypoint.longitude ? parseFloat(editingWaypoint.longitude) : null,
          is_mandatory: editingWaypoint.is_mandatory,
          radius_m: parseInt(editingWaypoint.radius_m) || 200
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Waypoint actualizado');
      const updated = waypoints.map(w => w.id === editingWaypoint.id ? response.data.waypoint : w);
      onWaypointsChange(updated);
      setEditingWaypoint(null);
    } catch (error) {
      console.error('Error updating waypoint:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar waypoint');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWaypoint = async (waypointId) => {
    if (!window.confirm('¿Eliminar este waypoint? Las fotos asociadas quedarán sin waypoint asignado.')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/events/${eventId}/waypoints/${waypointId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Waypoint eliminado');
      onWaypointsChange(waypoints.filter(w => w.id !== waypointId));
    } catch (error) {
      console.error('Error deleting waypoint:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar waypoint');
    }
  };

  const handleUploadReferencePhoto = async (waypointId, file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/events/${eventId}/waypoints/${waypointId}/photo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success('Foto de referencia subida');
      const updated = waypoints.map(w => 
        w.id === waypointId ? { ...w, reference_photo_url: response.data.photo_url } : w
      );
      onWaypointsChange(updated);
    } catch (error) {
      console.error('Error uploading reference photo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir foto');
    } finally {
      setUploading(false);
    }
  };

  const handleImportGpx = async (file) => {
    if (!file) return;
    
    setImportingGpx(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/events/${eventId}/waypoints/import-gpx`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      const { imported, skipped, waypoints: newWaypoints } = response.data;
      
      if (imported > 0) {
        toast.success(`Se importaron ${imported} waypoints desde el archivo GPX`);
        onWaypointsChange([...waypoints, ...newWaypoints]);
      }
      
      if (skipped && skipped.length > 0) {
        skipped.forEach(s => {
          toast.warning(`Omitido: ${s.name} - ${s.reason}`);
        });
      }
      
      if (imported === 0 && (!skipped || skipped.length === 0)) {
        toast.info('No se encontraron waypoints en el archivo GPX');
      }
      
    } catch (error) {
      console.error('Error importing GPX waypoints:', error);
      toast.error(error.response?.data?.detail || 'Error al importar waypoints desde GPX');
    } finally {
      setImportingGpx(false);
    }
  };

  const moveWaypoint = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= waypoints.length) return;

    const newOrder = [...waypoints];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    
    // Update order numbers
    newOrder.forEach((w, i) => w.order = i);
    onWaypointsChange(newOrder);

    // Save to backend
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/events/${eventId}/waypoints/reorder`,
        newOrder.map(w => w.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error reordering waypoints:', error);
    }
  };

  const sortedWaypoints = [...waypoints].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-orange-500" />
          Waypoints / Checkpoints ({waypoints.length})
        </h3>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.gpx,.kml,.kmz"
              className="hidden"
              onChange={handleImportFile}
              data-testid="waypoint-import-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={importing}
              onClick={(e) => { e.stopPropagation(); importInputRef.current?.click(); }}
              title="Importar desde Excel, CSV, GPX o KML/KMZ (Google My Maps)"
              data-testid="waypoint-import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Importar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={importing}
              onClick={handleImportUrl}
              title="Importar desde un enlace de Google My Maps (las capas se convierten en categorías)"
              className="border-blue-300 text-blue-700"
              data-testid="waypoint-import-url-btn"
            >
              <Link2 className="w-4 h-4 mr-1" />
              My Maps
            </Button>
            <a
              href={`${API}/admin/waypoints/import-template`}
              onClick={async (e) => { e.preventDefault(); e.stopPropagation(); await downloadTemplate(); }}
              className="text-xs text-orange-600 underline"
              data-testid="waypoint-template-link"
            >
              Plantilla
            </a>
            {/* Manual waypoint button */}
            <Button 
              onClick={(e) => { e.stopPropagation(); setShowAddModal(true); }} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600"
              type="button"
              data-testid="add-waypoint-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Añadir Manual
            </Button>
            
            {/* GPX Import button */}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".gpx"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleImportGpx(e.target.files[0]);
                    e.target.value = ''; // Reset input
                  }
                }}
                disabled={importingGpx}
                data-testid="import-gpx-input"
              />
              <Button 
                size="sm" 
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                asChild
                disabled={importingGpx}
                type="button"
              >
                <span data-testid="import-gpx-btn">
                  <Upload className="w-4 h-4 mr-1" />
                  {importingGpx ? 'Importando...' : 'Importar GPX'}
                </span>
              </Button>
            </label>
          </div>
        )}
      </div>

      {waypoints.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No hay waypoints definidos</p>
          {!readOnly && (
            <p className="text-sm text-gray-400 mt-1">
              Añade waypoints que los participantes deben fotografiar
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {sortedWaypoints.map((waypoint, index) => (
            <Card key={waypoint.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  {/* Order controls */}
                  {!readOnly && (
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0"
                        type="button"
                        onClick={() => moveWaypoint(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0"
                        type="button"
                        onClick={() => moveWaypoint(index, 'down')}
                        disabled={index === waypoints.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {/* Order number with type indicator */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white ${
                    waypoint.waypoint_type === 'start' ? 'bg-green-500' :
                    waypoint.waypoint_type === 'finish' ? 'bg-red-500' :
                    'bg-orange-500'
                  }`}>
                    {waypoint.waypoint_type === 'start' ? (
                      <Flag className="w-4 h-4" />
                    ) : waypoint.waypoint_type === 'finish' ? (
                      <FlagTriangleRight className="w-4 h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>

                  {/* Reference photo thumbnail */}
                  {waypoint.reference_photo_url ? (
                    <img 
                      src={waypoint.reference_photo_url} 
                      alt={waypoint.name}
                      className="w-12 h-12 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                      <Image className="w-5 h-5 text-gray-400" />
                    </div>
                  )}

                  {/* Waypoint info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{waypoint.name}</span>
                      {waypoint.category && (
                        <Badge className="text-xs text-white border-0" style={{ backgroundColor: waypoint.color || '#6b7280' }} data-testid={`wp-category-${waypoint.id}`}>
                          {waypoint.category}{waypoint.points ? ` · ${waypoint.points} pts` : ''}
                        </Badge>
                      )}
                      {waypoint.waypoint_type === 'start' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">Salida</Badge>
                      )}
                      {waypoint.waypoint_type === 'finish' && (
                        <Badge className="bg-red-100 text-red-700 text-xs">Llegada</Badge>
                      )}
                      {waypoint.waypoint_type === 'passage' && (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">Punto de Paso</Badge>
                      )}
                      {waypoint.waypoint_type === 'checkpoint' && waypoint.is_mandatory ? (
                        <Badge variant="default" className="bg-blue-100 text-blue-700 text-xs">Obligatorio</Badge>
                      ) : waypoint.waypoint_type === 'checkpoint' && (
                        <Badge variant="outline" className="text-xs">Opcional</Badge>
                      )}
                    </div>
                    {waypoint.description && (
                      <p className="text-sm text-gray-500 truncate">{waypoint.description}</p>
                    )}
                    {waypoint.latitude && waypoint.longitude && (
                      <p className="text-xs text-gray-400">
                        📍 {waypoint.latitude.toFixed(5)}, {waypoint.longitude.toFixed(5)} • Radio: {waypoint.radius_m || 150}m
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {!readOnly && (
                    <div className="flex items-center gap-1">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleUploadReferencePhoto(waypoint.id, e.target.files[0]);
                            }
                          }}
                          disabled={uploading}
                        />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" asChild>
                          <span>
                            <Camera className="w-4 h-4 text-blue-600" />
                          </span>
                        </Button>
                      </label>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0"
                        type="button"
                        onClick={() => setEditingWaypoint({ ...waypoint })}
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0"
                        type="button"
                        onClick={() => handleDeleteWaypoint(waypoint.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Waypoint Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              Añadir Waypoint
            </DialogTitle>
            <DialogDescription>
              Define un punto de control que los participantes deben pasar (detectado por GPS)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Waypoint Type Selector */}
            <div>
              <Label htmlFor="wp-type">Tipo de Waypoint *</Label>
              <Select
                value={newWaypoint.waypoint_type}
                onValueChange={handleWaypointTypeChange}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona el tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start" disabled={hasStart}>
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-green-500" />
                      Salida {hasStart && '(ya existe)'}
                    </div>
                  </SelectItem>
                  <SelectItem value="finish" disabled={hasFinish}>
                    <div className="flex items-center gap-2">
                      <FlagTriangleRight className="w-4 h-4 text-red-500" />
                      Llegada {hasFinish && '(ya existe)'}
                    </div>
                  </SelectItem>
                  <SelectItem value="checkpoint">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-500" />
                      Paso obligatorio
                    </div>
                  </SelectItem>
                  <SelectItem value="passage">
                    <div className="flex items-center gap-2">
                      <Circle className="w-4 h-4 text-yellow-500" />
                      Punto de paso
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {(newWaypoint.waypoint_type === 'start' || newWaypoint.waypoint_type === 'finish') && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ Los waypoints de Salida/Llegada requieren coordenadas GPS
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="wp-name">Nombre *</Label>
              <Input
                id="wp-name"
                placeholder="Ej: Castillo de Loarre"
                value={newWaypoint.name}
                onChange={(e) => setNewWaypoint({ ...newWaypoint, name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="wp-desc">Descripción / Instrucciones</Label>
              <Input
                id="wp-desc"
                placeholder="Ej: Fotografiar la entrada principal con la moto visible"
                value={newWaypoint.description}
                onChange={(e) => setNewWaypoint({ ...newWaypoint, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wp-lat">
                  Latitud {(newWaypoint.waypoint_type === 'start' || newWaypoint.waypoint_type === 'finish') ? '*' : '(opcional)'}
                </Label>
                <Input
                  id="wp-lat"
                  type="number"
                  step="any"
                  placeholder="42.12345"
                  value={newWaypoint.latitude}
                  onChange={(e) => setNewWaypoint({ ...newWaypoint, latitude: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="wp-lon">
                  Longitud {(newWaypoint.waypoint_type === 'start' || newWaypoint.waypoint_type === 'finish') ? '*' : '(opcional)'}
                </Label>
                <Input
                  id="wp-lon"
                  type="number"
                  step="any"
                  placeholder="-0.12345"
                  value={newWaypoint.longitude}
                  onChange={(e) => setNewWaypoint({ ...newWaypoint, longitude: e.target.value })}
                />
              </div>
            </div>

            {/* Tolerance radius */}
            <div>
              <Label htmlFor="wp-radius">Radio de tolerancia (metros) *</Label>
              <Input
                id="wp-radius"
                type="number"
                min="10"
                max="5000"
                step="10"
                placeholder="200"
                value={newWaypoint.radius_m}
                onChange={(e) => setNewWaypoint({ ...newWaypoint, radius_m: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                El piloto debe pasar a menos de {newWaypoint.radius_m || 200}m del waypoint para que se considere cumplido
              </p>
            </div>

            {newWaypoint.waypoint_type === 'checkpoint' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="wp-mandatory"
                  checked={newWaypoint.is_mandatory}
                  onChange={(e) => setNewWaypoint({ ...newWaypoint, is_mandatory: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="wp-mandatory" className="cursor-pointer">
                  Waypoint obligatorio (debe completarse para finalizar el tracking)
                </Label>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" type="button" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleAddWaypoint} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving ? 'Guardando...' : 'Añadir Waypoint'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Waypoint Modal */}
      <Dialog open={!!editingWaypoint} onOpenChange={() => setEditingWaypoint(null)}>
        <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-500" />
              Editar Waypoint
            </DialogTitle>
          </DialogHeader>

          {editingWaypoint && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-wp-name">Nombre *</Label>
                <Input
                  id="edit-wp-name"
                  value={editingWaypoint.name}
                  onChange={(e) => setEditingWaypoint({ ...editingWaypoint, name: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="edit-wp-desc">Descripción / Instrucciones</Label>
                <Input
                  id="edit-wp-desc"
                  value={editingWaypoint.description || ''}
                  onChange={(e) => setEditingWaypoint({ ...editingWaypoint, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-wp-lat">Latitud</Label>
                  <Input
                    id="edit-wp-lat"
                    type="number"
                    step="any"
                    value={editingWaypoint.latitude || ''}
                    onChange={(e) => setEditingWaypoint({ ...editingWaypoint, latitude: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-wp-lon">Longitud</Label>
                  <Input
                    id="edit-wp-lon"
                    type="number"
                    step="any"
                    value={editingWaypoint.longitude || ''}
                    onChange={(e) => setEditingWaypoint({ ...editingWaypoint, longitude: e.target.value })}
                  />
                </div>
              </div>

              {/* Tolerance radius */}
              <div>
                <Label htmlFor="edit-wp-radius">Radio de tolerancia (metros) *</Label>
                <Input
                  id="edit-wp-radius"
                  type="number"
                  min="10"
                  max="5000"
                  step="10"
                  placeholder="200"
                  value={editingWaypoint.radius_m || 200}
                  onChange={(e) => setEditingWaypoint({ ...editingWaypoint, radius_m: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  El piloto debe pasar a menos de {editingWaypoint.radius_m || 200}m del waypoint
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-wp-mandatory"
                  checked={editingWaypoint.is_mandatory}
                  onChange={(e) => setEditingWaypoint({ ...editingWaypoint, is_mandatory: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-wp-mandatory" className="cursor-pointer">
                  Waypoint obligatorio
                </Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" type="button" onClick={() => setEditingWaypoint(null)}>
                  Cancelar
                </Button>
                <Button type="button" onClick={handleUpdateWaypoint} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
